// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  encrypt, 
  decrypt, 
  hashPassword, 
  verifyPassword, 
  generateSecurePassword, 
  storeAutoPassword, 
  getAutoPassword 
} from '../utils/cryptoUtils';

// Create the context
export const AuthContext = createContext(null);

// Local storage keys
const AUTH_SETTINGS_KEY = 'bitdog_auth_settings';
const SESSION_TIMEOUT_KEY = 'bitdog_session_timeout';
const PASSWORD_DEFERRED_KEY = 'bitdog_password_deferred';
const AUTO_PASSWORD_KEY = 'bitdog_auto_password';
const SESSION_KEY = 'bitdog_session_active';
const PAGE_SESSION_KEY = 'bitdog_page_session';
const LAST_ACTIVITY_KEY = 'bitdog_last_activity'; // Key for tracking last activity
const ENCRYPTED_WALLETS_KEY = 'bitdog_encrypted_wallets';
const LEGACY_WALLETS_KEY = 'bitdogWallets';

// Default session timeout: 15 minutes
const DEFAULT_SESSION_TIMEOUT = 15 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  // Initialize translation
  const { t } = useTranslation();
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [hasAutoPassword, setHasAutoPassword] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [passwordHash, setPasswordHash] = useState(null);
  const [passwordSalt, setPasswordSalt] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [sessionTimeout, setSessionTimeout] = useState(DEFAULT_SESSION_TIMEOUT);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [passwordDeferred, setPasswordDeferred] = useState(false);
  const [sessionActive, _setSessionActive] = useState(false);
  const [pageSessionId, setPageSessionId] = useState('');
  
  // Explicitly expose the setSessionActive function to handle localStorage updates
  const setSessionActive = (active) => {
    try {
      console.log(`Setting session active: ${active}`);
      
      if (active) {
        // When activating a session, also update the last activity timestamp
        const now = Date.now();
        localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
        setLastActivity(now);
        localStorage.setItem(SESSION_KEY, 'true');
        
        // Clear any expired session marker (for mobile)
        localStorage.removeItem('bitdog_session_expired');
        
        // IMPORTANT: Directly set authentication state
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        
        // Special handling for mobile browsers to prevent wallet disappearance
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          console.log("Mobile device detected: marking session as expired rather than removing");
          localStorage.setItem('bitdog_session_expired', 'true');
        }
        
        // Clear authentication when session ends
        setIsAuthenticated(false);
      }
      
      // Update the state
      _setSessionActive(active);
    } catch (error) {
      console.error('Failed to update session state:', error);
    }
  };
  
  // Generate a unique session ID for this page load
  useEffect(() => {
    // Generate a unique session ID for this browser session
    const generateSessionId = () => {
      return Date.now().toString(36) + Math.random().toString(36).substr(2);
    };
    
    // Create a new session ID for this page load
    const newSessionId = generateSessionId();
    setPageSessionId(newSessionId);
    
    // Store the current page session ID
    try {
      localStorage.setItem(PAGE_SESSION_KEY, newSessionId);
    } catch (error) {
      console.error('Failed to handle page session:', error);
    }
  }, []);
  
  // Check for session timeout on initial load
  useEffect(() => {
    const checkSessionOnLoad = () => {
      const activeSession = localStorage.getItem(SESSION_KEY) === 'true';
      const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
      
      if (activeSession && lastActivityStr) {
        const lastActivityTime = parseInt(lastActivityStr, 10);
        const now = Date.now();
        const inactivityTime = now - lastActivityTime;
        
        // Load the timeout setting
        let timeoutDuration = DEFAULT_SESSION_TIMEOUT;
        const storedTimeout = localStorage.getItem(SESSION_TIMEOUT_KEY);
        if (storedTimeout) {
          timeoutDuration = parseInt(storedTimeout, 10) || DEFAULT_SESSION_TIMEOUT;
        }
        
        // Update the state with the loaded timeout
        setSessionTimeout(timeoutDuration);
        
        // Check if session has timed out
        if (inactivityTime > timeoutDuration) {
          console.log(`Session timed out after ${inactivityTime}ms of inactivity (limit: ${timeoutDuration}ms)`);
          
          // Handle differently on mobile devices to prevent wallet disappearance
          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
          if (isMobile) {
            console.log("Mobile device detected: marking session as expired rather than removing");
            localStorage.setItem('bitdog_session_expired', 'true');
          }
          
          localStorage.removeItem(SESSION_KEY);
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          _setSessionActive(false);
          setIsAuthenticated(false);
          return false;
        } else {
          console.log(`Session is still valid: ${inactivityTime}ms inactive (limit: ${timeoutDuration}ms)`);
          // Update last activity for this new pageload
          const now = Date.now();
          localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
          setLastActivity(now);
          return true;
        }
      }
      
      // Also check for expired mobile sessions
      const expiredMobileSession = localStorage.getItem('bitdog_session_expired') === 'true';
      if (expiredMobileSession) {
        console.log("Found expired mobile session, wallet should be restored after authentication");
        return false;
      }
      
      return false;
    };
    
    // Check session validity immediately
    checkSessionOnLoad();
  }, []);
  
  // Load auth settings on mount and reset authentication state
  useEffect(() => {
    const initializeAuthState = () => {
      try {
        // Load auth settings from local storage
        const storedSettings = localStorage.getItem(AUTH_SETTINGS_KEY);
        if (storedSettings) {
          const settings = JSON.parse(storedSettings);
          if (settings && settings.passwordHash && settings.passwordSalt) {
            setPasswordHash(settings.passwordHash);
            setPasswordSalt(settings.passwordSalt);
            setHasPassword(true);
          }
        }
        
        // Load session timeout setting
        const storedTimeout = localStorage.getItem(SESSION_TIMEOUT_KEY);
        if (storedTimeout) {
          setSessionTimeout(parseInt(storedTimeout, 10) || DEFAULT_SESSION_TIMEOUT);
        }
        
        // Load password deferred flag
        const deferredFlag = localStorage.getItem(PASSWORD_DEFERRED_KEY);
        if (deferredFlag) {
          setPasswordDeferred(deferredFlag === 'true');
        }
        
        // Check if we have an auto-generated password
        const hasAuto = localStorage.getItem(AUTO_PASSWORD_KEY) !== null;
        setHasAutoPassword(hasAuto);
        
        // Check for active session and timeout
        const activeSession = localStorage.getItem(SESSION_KEY) === 'true';
        const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
        
        if (activeSession && lastActivityStr) {
          const lastActivityTime = parseInt(lastActivityStr, 10);
          const now = Date.now();
          const inactivityTime = now - lastActivityTime;
          
          if (inactivityTime > sessionTimeout) {
            // Session has timed out due to inactivity
            console.log(`Session timed out after ${inactivityTime}ms of inactivity`);
            localStorage.removeItem(SESSION_KEY);
            localStorage.removeItem(LAST_ACTIVITY_KEY);
            _setSessionActive(false);
            setIsAuthenticated(false);
          } else {
            // Session is still valid
            console.log('Active session found during initialization');
            _setSessionActive(true);
            setIsAuthenticated(true);
            // Update last activity
            localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
            setLastActivity(now);
          }
        } else {
          console.log('No active session found during initialization');
          _setSessionActive(false);
          setIsAuthenticated(false);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to load auth settings:', error);
        setIsInitialized(true);
      }
    };
    
    initializeAuthState();
    
    // Reset auth state when the page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // When returning to the page, check if session is still active
        const currentSession = localStorage.getItem(SESSION_KEY);
        const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
        
        if (currentSession === 'true' && lastActivityStr) {
          const lastActivityTime = parseInt(lastActivityStr, 10);
          const now = Date.now();
          const inactivityTime = now - lastActivityTime;
          
          if (inactivityTime > sessionTimeout) {
            // Session has timed out due to inactivity
            console.log(`Session timed out while page was inactive: ${inactivityTime}ms`);
            localStorage.removeItem(SESSION_KEY);
            localStorage.removeItem(LAST_ACTIVITY_KEY);
            _setSessionActive(false);
            setIsAuthenticated(false);
          } else {
            // Session is still active - update activity timestamp
            localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
            setLastActivity(now);
          }
        } else if (currentSession !== 'true') {
          console.log('Session not active on visibility change, requiring re-authentication');
          setIsAuthenticated(false);
          _setSessionActive(false);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionTimeout]);
  
  // Listen for storage events (changes in another tab)
  useEffect(() => {
    const handleStorageChange = (event) => {
      // If auth settings were changed in another tab
      if (event.key === AUTH_SETTINGS_KEY || 
          event.key === SESSION_TIMEOUT_KEY || 
          event.key === SESSION_KEY || 
          event.key === LAST_ACTIVITY_KEY) {
        console.log("Auth settings changed in another tab:", event.key);
        
        if (event.key === SESSION_KEY && event.newValue !== 'true') {
          // Force logout in this tab for security
          console.log("Session ended in another tab, logging out in this tab");
          setIsAuthenticated(false);
          _setSessionActive(false);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Save auth settings when they change
  useEffect(() => {
    if (!isInitialized) return;
    
    try {
      if (passwordHash && passwordSalt) {
        localStorage.setItem(AUTH_SETTINGS_KEY, JSON.stringify({
          passwordHash,
          passwordSalt,
          createdAt: Date.now()
        }));
      }
    } catch (error) {
      console.error('Failed to save auth settings:', error);
    }
  }, [passwordHash, passwordSalt, isInitialized]);
  
  // Save session timeout when it changes
  useEffect(() => {
    if (!isInitialized) return;
    
    try {
      localStorage.setItem(SESSION_TIMEOUT_KEY, sessionTimeout.toString());
    } catch (error) {
      console.error('Failed to save session timeout:', error);
    }
  }, [sessionTimeout, isInitialized]);
  
  // Session timeout check
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const checkSessionTimeout = async () => {
      const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
      
      if (lastActivityStr) {
        const lastActivityTime = parseInt(lastActivityStr, 10);
        const now = Date.now();
        if (now - lastActivityTime > sessionTimeout) {
          // Session expired
          console.log("Session timeout detected");
          
          // For auto-security users, try to auto-reauth immediately
          if (passwordDeferred && hasAutoPassword) {
            console.log("Auto-security user detected, attempting automatic re-authentication");
            try {
              const success = await autoAuthenticateWithDeferred();
              if (success) {
                console.log("Auto re-authentication successful after timeout");
                // Update activity timestamp to reset the timer
                const newTime = Date.now();
                localStorage.setItem(LAST_ACTIVITY_KEY, newTime.toString());
                setLastActivity(newTime);
                return; // Skip the logout if auto-auth succeeded
              } else {
                console.log("Auto re-authentication failed, proceeding with logout");
              }
            } catch (err) {
              console.error("Auto re-authentication failed:", err);
            }
          }
          
          // If we get here, we need to log out
          logout();
        }
      }
    };
    
    // Check every minute
    const interval = setInterval(checkSessionTimeout, 60 * 1000);
    
    // Cleanup
    return () => {
      clearInterval(interval);
    };
  }, [isAuthenticated, sessionTimeout, passwordDeferred, hasAutoPassword]);
  
  // Track user activity
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const updateActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      // Also update in localStorage for persistence across page refreshes
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    };
    
    // Update activity on user interaction
    window.addEventListener('click', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('touchstart', updateActivity);
    
    // Cleanup
    return () => {
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [isAuthenticated]);
  
  /**
   * Create a new password
   * @param {string} password - Password to set
   * @returns {Promise<boolean>} - Success status
   */
  const createPassword = async (password) => {
    try {
      // Hash the password for storage and verification
      const { hash, salt } = await hashPassword(password);
      
      // Update state
      setPasswordHash(hash);
      setPasswordSalt(salt);
      setHasPassword(true);
      setIsAuthenticated(true);
      setSessionActive(true);
      setLastActivity(Date.now());
      setAuthError(null);
      
      return true;
    } catch (error) {
      console.error('Failed to create password:', error);
      setAuthError(t('auth.errors.createPasswordFailed'));
      return false;
    }
  };

/**
 * Remove the user password authentication requirement
 * This maintains wallet encryption with auto-password but removes manual password entry
 * @param {string} currentPassword - Current password for verification
 * @returns {Promise<boolean>} - Success status
 */
const removePassword = async (currentPassword) => {
  try {
    // First verify the current password
    if (!passwordHash || !passwordSalt) {
      setAuthError(t('auth.errors.noPasswordSet'));
      return false;
    }
    
    const isValid = await verifyPassword(currentPassword, passwordHash, passwordSalt);
    
    if (!isValid) {
      setAuthError(t('auth.errors.incorrectPassword'));
      return false;
    }
    
    // Ensure we have an auto-password for wallet encryption
    if (!hasAutoPassword) {
      // If no auto-password exists, create one first
      const { success } = await createAutoPassword();
      if (!success) {
        setAuthError(t('auth.errors.securitySetupFailed'));
        return false;
      }
    }
    
    // Clear password hash and salt
    setPasswordHash(null);
    setPasswordSalt(null);
    setHasPassword(false);
    
    // Set password as deferred (indicating no password is required)
    deferPassword(true);
    
    // Remove password data from localStorage
    try {
      const settings = JSON.parse(localStorage.getItem(AUTH_SETTINGS_KEY) || '{}');
      delete settings.passwordHash;
      delete settings.passwordSalt;
      localStorage.setItem(AUTH_SETTINGS_KEY, JSON.stringify(settings));
      
      // Broadcast a message to update other tabs
      localStorage.setItem('bitdog_auth_update', Date.now().toString());
    } catch (error) {
      console.error('Failed to update auth settings:', error);
      // Continue anyway - the state is already updated
    }
    
    console.log('Password removed successfully, new auth state:', {
      hasPassword: false,
      passwordDeferred: true,
      hasPasswordHash: false,
      hasPasswordSalt: false
    });
    
    setAuthError(null);
    return true;
  } catch (error) {
    console.error('Failed to remove password:', error);
    setAuthError(t('auth.errors.removePasswordFailed'));
    return false;
  }
};
  
  /**
   * Create an auto-generated password for wallet encryption
   * UPDATED: Simplified to always store in retrievable format
   * @returns {Promise<{success: boolean, password: string}>} - Result with generated password
   */
  const createAutoPassword = async (userPassword = null) => {
    try {
      // Generate a strong password
      const autoPassword = generateSecurePassword();
      
      // Store it securely - now passing the userPassword if available
      await storeAutoPassword(autoPassword, userPassword);
      
      setHasAutoPassword(true);
      
      return { success: true, password: autoPassword };
    } catch (error) {
      console.error('Failed to create auto password:', error);
      return { success: false, password: null };
    }
  };

  /**
   * SIMPLIFIED: No longer needed since auto-password is not encrypted with user password
   * Only kept for backward compatibility
   */
  const setupDualLayerSecurity = async (userPassword) => {
    console.log("Setup dual-layer security called, but using simplified security model");
    // In the new model, we don't need to do anything special here
    return true;
  };
  
  /**
   * Verify and log in with password
   * @param {string} password - Password to verify
   * @returns {Promise<boolean>} - Success status
   */
  const login = async (password) => {
    if (!passwordHash || !passwordSalt) {
      setAuthError(t('auth.errors.noPasswordSetup'));
      return false;
    }
    
    try {
      const isValid = await verifyPassword(password, passwordHash, passwordSalt);
      
      if (isValid) {
        // Password is valid, set authenticated state
        setIsAuthenticated(true);
        setSessionActive(true);
        setLastActivity(Date.now());
        setAuthError(null);
        return true;
      } else {
        setAuthError(t('auth.errors.incorrectPassword'));
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(t('auth.errors.verifyPasswordFailed'));
      return false;
    }
  };
  
  /**
   * Log out and clear authentication state
   */
  const logout = () => {
    console.log('Logging out, clearing authentication state');
    setIsAuthenticated(false);
    setSessionActive(false);
  };
  
  /**
   * Verify active session for critical operations
   * @returns {boolean} - Whether session is valid
   */
  const verifySession = () => {
    if (!isAuthenticated) {
      throw new Error(t('auth.errors.sessionExpired'));
    }
    
    // Check if session has timed out
    const now = Date.now();
    const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
    const lastActivityTime = lastActivityStr ? parseInt(lastActivityStr, 10) : lastActivity;
    
    if (now - lastActivityTime > sessionTimeout) {
      logout();
      throw new Error(t('auth.errors.sessionTimeout'));
    }
    
    // Update activity timestamp
    setLastActivity(now);
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    return true;
  };
  
  /**
   * Change existing password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password to set
   * @returns {Promise<boolean>} - Success status
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      // Verify current password
      const isValid = await verifyPassword(currentPassword, passwordHash, passwordSalt);
      
      if (!isValid) {
        setAuthError(t('auth.errors.currentPasswordIncorrect'));
        return false;
      }
      
      // Hash new password
      const { hash, salt } = await hashPassword(newPassword);
      
      // Update state
      setPasswordHash(hash);
      setPasswordSalt(salt);
      setAuthError(null);
      
      return true;
    } catch (error) {
      console.error('Failed to change password:', error);
      setAuthError(t('auth.errors.changePasswordFailed'));
      return false;
    }
  };
  
  /**
   * Reset password (requires wallet recovery) - not implemented yet
   */
  const resetPassword = async () => {
    // This would typically be implemented with an additional recovery mechanism
    // like seed phrase verification
    setAuthError(t('auth.errors.resetRequiresRecovery'));
    return false;
  };
  
  /**
   * Update session timeout duration
   * @param {number} minutes - Timeout in minutes
   */
  const updateSessionTimeout = (minutes) => {
    try {
      if (minutes < 1) minutes = 1;
      const timeoutMs = minutes * 60 * 1000;
      
      // Update state
      setSessionTimeout(timeoutMs);
      
      // Update in storage
      localStorage.setItem(SESSION_TIMEOUT_KEY, timeoutMs.toString());
      
      console.log(`Session timeout updated to ${minutes} minutes (${timeoutMs}ms)`);
      
      // If we have an active session, reset the timer with new duration
      if (localStorage.getItem(SESSION_KEY) === 'true') {
        // Update last activity timestamp to now to start fresh timeout
        const now = Date.now();
        localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
        setLastActivity(now);
        console.log(`Activity timestamp reset to apply new timeout setting`);
      }
    } catch (error) {
      console.error('Failed to update session timeout:', error);
    }
  };
  
  /**
   * Encrypt sensitive data using the current password
   * @param {string} data - Data to encrypt
   * @param {string} password - Password for encryption
   * @returns {Promise<string>} - Encrypted data
   */
  const encryptData = async (data, password) => {
    try {
      return await encrypt(data, password);
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error(t('auth.errors.encryptionFailed'));
    }
  };
  
  /**
   * Decrypt sensitive data using the current password
   * @param {string} encryptedData - Encrypted data
   * @param {string} password - Password for decryption
   * @returns {Promise<string>} - Decrypted data
   */
  const decryptData = async (encryptedData, password) => {
    try {
      return await decrypt(encryptedData, password);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error(t('auth.errors.decryptionFailed'));
    }
  };
  
  /**
   * Set password deferred flag
   * @param {boolean} deferred - Whether to defer password creation
   */
  const deferPassword = (deferred = true) => {
    setPasswordDeferred(deferred);
    try {
      localStorage.setItem(PASSWORD_DEFERRED_KEY, deferred.toString());
    } catch (error) {
      console.error('Failed to save password deferred flag:', error);
    }
  };
  

/**
 * Reset wallet completely - emergency recovery option
 * Clears all wallets, passwords, and encrypted data
 */
const resetWalletApp = () => {
  // Show critical warning before proceeding - restore double confirmation
  const confirmed = window.confirm(
    t('auth.resetWallet.criticalWarning')
  );
  
  if (!confirmed) return false;
  
  // Double-confirm to prevent accidental clicks
  const doubleConfirmed = window.confirm(
    t('auth.resetWallet.confirmationPrompt')
  );
  
  if (!doubleConfirmed) return false;
  
  try {
    console.log("RESETTING WALLET APP - CLEARING ALL DATA");
    
    // Clear all flags that might be set
    sessionStorage.removeItem('bitdog_wallet_created');
    sessionStorage.setItem('bitdog_fresh_reset', 'true');
    
    // Set clean reset flag BEFORE clearing localStorage
    sessionStorage.setItem('clean_reset', 'true');
    
    // CRITICAL: First explicitly remove the problematic key
    console.log("Explicitly removing bitdog_encrypted_wallets");
    localStorage.removeItem('bitdog_encrypted_wallets');
    
    // FORCEFULLY CLEAR ALL LOCALSTORAGE
    localStorage.clear();
    
    // Verify localStorage is empty
    if (localStorage.length > 0) {
      console.error("Warning: localStorage not completely cleared, trying again");
      
      // For each remaining key, remove it individually
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        console.log(`Removing remaining key: ${key}`);
        localStorage.removeItem(key);
      }
      
      // Clear again
      localStorage.clear();
    }
    
    // Create a temporary element to show reset in progress
    const resetMessage = document.createElement('div');
    resetMessage.style.position = 'fixed';
    resetMessage.style.top = '0';
    resetMessage.style.left = '0';
    resetMessage.style.width = '100%';
    resetMessage.style.height = '100%';
    resetMessage.style.backgroundColor = 'black';
    resetMessage.style.color = 'white';
    resetMessage.style.display = 'flex';
    resetMessage.style.alignItems = 'center';
    resetMessage.style.justifyContent = 'center';
    resetMessage.style.zIndex = '10000';
    resetMessage.style.fontSize = '18px';
    resetMessage.textContent = t('auth.resetWallet.resetting');
    document.body.appendChild(resetMessage);
    
    // Force a complete page reload and navigation to root
    setTimeout(() => {
      // Hard reload to ensure clean state
      window.location.replace('/?fresh=' + Date.now());
    }, 500);
    
    return true;
  } catch (error) {
    console.error('Failed to reset wallet app:', error);
    alert(t('auth.resetWallet.errorResetting', { error: error.message }));
    return false;
  }
};

/**
 * Auto-authenticate when password is deferred or using auto-security
 * IMPROVED: Enhanced for automatic login without password modal
 */
const autoAuthenticateWithDeferred = async () => {
  try {
    console.log("Attempting auto-authentication");
    
    // Check important conditions
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const expiredMobileSession = localStorage.getItem('bitdog_session_expired') === 'true';
    const hasAutoPasswordStored = localStorage.getItem(AUTO_PASSWORD_KEY) !== null;
    
    if (!hasAutoPasswordStored) {
      console.error("Auto-authentication failed: no auto-password stored");
      return false;
    }
    
    // Get the auto-generated password
    const autoPassword = await getAutoPassword();
    
    if (!autoPassword) {
      console.error("Auto-authentication failed: could not retrieve auto-password");
      return false;
    }
    
    // Set session as active
    setSessionActive(true);
    setIsAuthenticated(true);
    
    // Update timestamp
    const now = Date.now();
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    setLastActivity(now);
    
    // Clear any expired session marker
    if (expiredMobileSession) {
      console.log("Clearing expired mobile session marker after successful authentication");
      localStorage.removeItem('bitdog_session_expired');
    }
    
    console.log("Auto-authentication successful");
    
    return true;
  } catch (error) {
    console.error("Auto-authentication failed:", error);
    return false;
  }
};
  
return (
  <AuthContext.Provider
    value={{
      isAuthenticated,
      hasPassword,
      hasAutoPassword,
      isInitialized,
      authError,
      sessionTimeout,
      passwordDeferred,
      sessionActive,
      pageSessionId,
      createPassword,
      createAutoPassword,
      setupDualLayerSecurity,
      login,
      logout,
      changePassword,
      removePassword,
      resetPassword,
      updateSessionTimeout,
      deferPassword,
      encryptData,
      decryptData,
      verifyPassword,
      verifySession,
      setSessionActive,
      resetWalletApp,
      autoAuthenticateWithDeferred,
      passwordHash,
      passwordSalt
    }}
  >
    {children}
  </AuthContext.Provider>
);
};